import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@mui/material';
import api from '../api';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Asia/Tokyo');

function formatChatTime(utcTime) {
  if (!utcTime) return '';
  const jst = dayjs.utc(utcTime).tz('Asia/Tokyo');
  const diffMin = dayjs().diff(jst, 'minute');
  if (diffMin < 60) return `${diffMin}分前`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)}時間前`;
  return jst.format('MM/DD HH:mm');
}

function formatFileSize(bytes) {
  if (bytes == null || Number.isNaN(Number(bytes))) return 'N/A';
  const b = Number(bytes);
  if (b < 1024) return `${b} B`;
  const kb = b / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  const gb = mb / 1024;
  return `${gb.toFixed(2)} GB`;
}

export default function ExternalChat() {
  const { token } = useParams();

  const [password, setPassword] = useState('');
  const [authed, setAuthed] = useState(false);

  const [chatRoomId, setChatRoomId] = useState(null);

  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);

  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadTarget, setDownloadTarget] = useState(null);

  const messagesEndRef = useRef(null);

  const canSend = useMemo(() => {
    return authed && !sending && (!!message.trim() || !!file);
  }, [authed, sending, message, file]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const externalHeaders = useMemo(() => {
    return {
      headers: {
        'x-external-token': token,
      },
    };
  }, [token]);

  const login = async () => {
    const res = await api.post('/api/external/chats/login', {
      token,
      password,
    });
    setChatRoomId(res.data.chatRoomId);
    setAuthed(true);
  };

  const loadMessages = async () => {
    setLoadingMessages(true);
    try {
      const res = await api.get(`/api/external/chats/${token}/messages`, externalHeaders);
      setMessages(res.data || []);
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    if (!authed) return;
    loadMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const sendMessage = async () => {
    if (!canSend) return;

    setSending(true);
    try {
      const formData = new FormData();
      formData.append('message', message || '');
      if (file) formData.append('file', file);

      await api.post(`/api/external/chats/${token}/messages`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'x-external-token': token,
        },
      });

      setMessage('');
      setFile(null);
      await loadMessages();
    } finally {
      setSending(false);
    }
  };

  const openDownloadModal = async (fileId) => {
    if (!fileId) return;

    setDownloadOpen(true);
    setDownloadLoading(true);
    setDownloadTarget(null);

    try {
      const res = await api.get(
        `/api/external/chats/${token}/files/${fileId}/download`,
        externalHeaders
      );
      setDownloadTarget(res.data);
    } catch {
      setDownloadTarget({ error: true });
    } finally {
      setDownloadLoading(false);
    }
  };

  const closeDownloadModal = () => {
    setDownloadOpen(false);
    setDownloadTarget(null);
    setDownloadLoading(false);
  };

  if (!authed) {
    return (
      <Paper sx={{ p: 3, maxWidth: 420, mx: 'auto', mt: 10 }}>
        <Typography variant="h6">チャットに参加</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          パスワードを入力してください。
        </Typography>

        <TextField
          fullWidth
          type="password"
          label="パスワード"
          sx={{ mt: 2 }}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />

        <Button
          fullWidth
          sx={{ mt: 2 }}
          variant="contained"
          onClick={() => login().catch(() => { })}
          disabled={!password}
        >
          入室
        </Button>
      </Paper>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 900, mx: 'auto' }}>
      <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '80vh' }}>
        <Typography variant="h6">チャット</Typography>
        <Typography variant="caption" color="text.secondary">
          ルームID: {chatRoomId}
        </Typography>
        <Divider sx={{ my: 1 }} />

        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            p: 1,
            border: '1px solid #ddd',
            borderRadius: 1,
            background: '#fafafa',
          }}
        >
          {loadingMessages && messages.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              読み込み中...
            </Typography>
          )}

          {!loadingMessages && messages.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              まだメッセージはありません。
            </Typography>
          )}

          {messages.map((m) => {
            const mine = m.senderId == null;

            return (
              <Box
                key={m.id}
                sx={{
                  mb: 1.5,
                  display: 'flex',
                  flexDirection: mine ? 'row-reverse' : 'row',
                }}
              >
                <Paper
                  sx={{
                    px: 1.5,
                    py: 1,
                    maxWidth: '80%',
                    background: mine ? '#e8f5e9' : '#fff',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {mine ? 'あなた' : (m.senderName || '管理者')}
                  </Typography>

                  {m.message && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {m.message}
                    </Typography>
                  )}

                  {m.fileId && (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 0.5,
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        color: 'primary.main',
                      }}
                      onClick={() => openDownloadModal(m.fileId)}
                    >
                      添付: {m.fileName || 'ファイル'}
                    </Typography>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    {formatChatTime(m.createdAt)}
                  </Typography>
                </Paper>
              </Box>
            );
          })}

          <div ref={messagesEndRef} />
        </Box>

        {file && (
          <Paper
            variant="outlined"
            sx={{
              p: 1,
              mt: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: '#f5f5f5',
            }}
          >
            <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
              添付予定: {file.name}（{formatFileSize(file.size)}）
            </Typography>
            <Button size="small" color="error" onClick={() => setFile(null)} disabled={sending}>
              削除
            </Button>
          </Paper>
        )}

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 1 }}>
          <Button component="label" variant="outlined" size="small" disabled={sending}>
            添付
            <input
              type="file"
              hidden
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </Button>

          <TextField
            fullWidth
            placeholder="メッセージを入力..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={sending}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                sendMessage().catch(() => { });
              }
            }}
          />

          <Button variant="contained" onClick={() => sendMessage().catch(() => { })} disabled={!canSend}>
            {sending ? (
              <>
                <CircularProgress size={16} sx={{ mr: 1 }} />
                送信中
              </>
            ) : (
              '送信'
            )}
          </Button>
        </Box>
      </Paper>

      <Dialog open={downloadOpen} onClose={closeDownloadModal} maxWidth="sm" fullWidth>
        <DialogTitle>ファイルをダウンロード</DialogTitle>
        <DialogContent dividers>
          {downloadLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2">準備中...</Typography>
            </Box>
          )}

          {!downloadLoading && downloadTarget?.error && (
            <Typography variant="body2" color="error">
              ダウンロード情報の取得に失敗しました。
            </Typography>
          )}

          {!downloadLoading && downloadTarget && !downloadTarget.error && (
            <>
              <Typography variant="body2">ファイル名: {downloadTarget.fileName}</Typography>
              <Typography variant="body2">ファイルサイズ: {formatFileSize(downloadTarget.fileSize)}</Typography>
              <Typography variant="body2">有効期限: {downloadTarget.expiresIn}</Typography>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                ※ ダウンロードURLは一時的なものです。再度ダウンロードする場合は、この画面を再読み込みしてください。
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDownloadModal} disabled={downloadLoading}>
            キャンセル
          </Button>
          <Button
            variant="contained"
            disabled={downloadLoading || !downloadTarget || downloadTarget?.error}
            onClick={() => {
              if (!downloadTarget?.signedUrl) return;
              window.open(downloadTarget.signedUrl, '_blank', 'noopener,noreferrer');
              closeDownloadModal();
            }}
          >
            ダウンロード
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
